import { PageHero } from "@/components/PageHero";
import { useLang } from "@/i18n/LanguageContext";
import { Reveal } from "@/components/motion";
import { EnquiryForm } from "@/components/EnquiryForm";
import { CtaBand } from "@/components/CtaBand";
import { usePageMeta } from "@/lib/seo";
import { IMG } from "@/lib/site";
import {
    CalendarClock,
    Wallet,
    Plane,
    Home,
    UserRoundCheck,
    ClipboardList,
    Building2,
    HeartHandshake,
} from "lucide-react";

const COVERS = [
    {
        icon: ClipboardList,
        title: "Expert review",
        desc: "Your case, reports and history coordinated for review by appropriate specialists.",
    },
    {
        icon: HeartHandshake,
        title: "Treatment options",
        desc: "Information on the recommended pathway and, where relevant, alternatives to discuss.",
    },
    {
        icon: CalendarClock,
        title: "Expected timeline",
        desc: "How the stages of treatment could fit together — consultations, procedures, recovery.",
    },
    {
        icon: Wallet,
        title: "Estimated costs",
        desc: "Cost information coordinated from providers where verified. Estimates are never guarantees.",
    },
    {
        icon: UserRoundCheck,
        title: "Specialists & tests",
        desc: "The specialists, tests and diagnostics your case calls for, coordinated with providers.",
    },
    {
        icon: Building2,
        title: "Hospital options",
        desc: "Coordinated provider options suited to your case, preferences and budget.",
    },
    {
        icon: Plane,
        title: "Travel & stay guidance",
        desc: "When to travel, how long to plan for, and where to stay — coordinated with local partners.",
    },
    {
        icon: Home,
        title: "Follow-up",
        desc: "Recovery, reviews and follow-up consultations planned — including after you return home.",
    },
];

export const TreatmentPlanning = () => {
    const { t, tHtml } = useLang();
    usePageMeta(
        t("meta.treatment_planning.title", "Treatment Planning"),
        t("meta.treatment_planning.desc", "A clear plan for a healthier tomorrow — pathway, timeline, cost information and travel guidance, coordinated by UniversHeal for your treatment in Kerala.")
    );
    return (
        <>
            <PageHero
                eyebrow={t("treatment_planning.eyebrow", "Treatment Planning")}
                title={
                    <>
                        A clear plan for a{" "}
                        <span className="italic text-pine">healthier tomorrow.</span>
                    </>
                }
                intro={t("treatment_planning.intro", "Good decisions need the full picture. We help you understand the recommended pathway, practical requirements and what to arrange — before you commit to anything.")}
            />

            <section className="pb-8">
                <div className="container-site">
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                        {COVERS.map((item, i) => (
                            <Reveal key={item.title} delay={Math.min(i * 0.05, 0.3)} className="h-full">
                                <div
                                    data-testid={`planning-cover-${i + 1}`}
                                    className="h-full bg-white border border-pine/10 rounded-2xl p-7"
                                >
                                    <item.icon className="w-6 h-6 text-forest" strokeWidth={1.5} />
                                    <h3 className="mt-5 font-serif font-medium text-xl text-forest">
                                        {item.title}
                                    </h3>
                                    <p className="mt-2.5 text-sm leading-relaxed text-moss">
                                        {item.desc}
                                    </p>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                    <Reveal>
                        <p className="mt-8 text-xs leading-relaxed text-moss max-w-3xl">
                            {t("treatment_planning.text10", "All treatment information, timelines and cost estimates are coordinated from independent providers and are indicative only — they are not medical advice or guarantees. Decisions are made with your treating specialists.")}
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="pb-8">
                <div className="container-site">
                    <Reveal>
                        <div className="rounded-2xl overflow-hidden h-64 sm:h-[420px] shadow-[0_30px_70px_rgba(6,60,53,0.14)]">
                            <img
                                src={IMG.planning}
                                alt="An Indian doctor explaining a treatment plan to an international couple"
                                data-testid="treatment-planning-image"
                                className="w-full h-full object-cover"
                            />
                        </div>
                    </Reveal>
                </div>
            </section>

            <section className="section-pad pt-8">
                <div className="container-site max-w-3xl">
                    <Reveal>
                        <EnquiryForm
                            service="Treatment Planning"
                            submitLabel={t("treatment_planning.submitLabel1", "Start Treatment Planning")}
                            testIdPrefix="treatment-planning-form"
                        />
                    </Reveal>
                </div>
            </section>

            <CtaBand
                headline={t("treatment_planning.headline", "Prefer to talk it through first?")}
                text={t("treatment_planning.text", "Reach us on WhatsApp or send a message — we will help you understand the next steps.")}
            />
        </>
    );
};
